import { Box, Container, Typography, Card, CardContent, Button, Grid, Stack, useMediaQuery, useTheme } from '@mui/material';
import { psiTokens } from '../theme/psiTheme';
import PageHeader from '../components/PageHeader';
import ServicesBanner from '../components/ServicesBanner';
import { usePageMeta } from '../hooks/usePageMeta';

const services = [
  {
    title: 'Six Sigma',
    description: 'Yellow, Green & Black Belt certifications plus real-project mentorship applying the full Six Sigma methodology.',
  },
  {
    title: 'Lean Manufacturing',
    description: 'Training through classes, workshops, and manufacturing simulators. Eliminate waste and optimize flow — hands-on, not just theory.',
  },
  {
    title: 'Minitab Training',
    description: 'Statistical analysis, process charts, and results interpretation for engineers and improvement teams. Data-driven decision making.',
  },
  {
    title: 'Problem Solving',
    description: 'QRQC, A3, PDCA, 8D, 7 Quality Tools, FMEA, APQP, and Toyota Kata. Structured methodologies to solve problems from the root cause.',
  },
  {
    title: 'Data Analysis with Excel',
    description: 'Statistical and graphical analysis using Excel and Power BI. Control charts, descriptive analysis, and KPI visualization.',
  },
  {
    title: 'New Employee Onboarding',
    description: 'Generic industrial concepts plus client-specific topics. Ready-to-use onboarding content for HR or internal training teams.',
  },
  {
    title: 'Lean Simulators',
    description: 'Operate a simulated production line, identify waste, propose improvements, and measure impact. Accelerates Lean culture adoption.',
  },
];

export default function ServicesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  usePageMeta(
    'Services',
    "PSI's training and consulting services: Six Sigma, Lean Manufacturing, Minitab training, structured problem solving, data analysis, and more, on-site, online, or hybrid."
  );

  return (
    <Box sx={{ backgroundColor: psiTokens.background.primary }}>
      <PageHeader
        eyebrow="What PSI Offers"
        title="Training & Consulting Services"
        subtitle="Available on-site, online, or hybrid. In English or Spanish. Adapted to each client's context."
        titleMaxWidth="900px"
      />

      <Container maxWidth="xl" sx={{ py: isMobile ? 6 : 8 }}>
        <Grid container spacing={3} justifyContent="center" sx={{ mb: 5 }}>
          {services.map((service, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: psiTokens.background.secondary,
                  borderRadius: '12px',
                  border: `1px solid ${psiTokens.border}`,
                  borderTop: `4px solid ${psiTokens.primary.dark}`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: psiTokens.text.primary, marginBottom: 2, fontWeight: 700, fontSize: '20px' }}
                  >
                    {service.title}
                  </Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 400, color: psiTokens.text.secondary, lineHeight: 1.75, marginBottom: 3 }}>
                    {service.description}
                  </Typography>

                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="contained"
                      sx={{
                        background: `linear-gradient(90deg, ${psiTokens.primary.dark}, ${psiTokens.primary.light})`,
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        flex: 1,
                        py: 1,
                      }}
                    >
                      Quote
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: psiTokens.primary.light,
                        color: psiTokens.primary.light,
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        flex: 1,
                        py: 1,
                        '&:hover': { backgroundColor: 'rgba(167, 111, 204, 0.05)' },
                      }}
                    >
                      Learn More
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <ServicesBanner />
      </Container>
    </Box>
  );
}
